import {calculateLayout} from '@gongxigames/framework/core/fragments/display/TextBox/calculateLayout';
import {Container, Graphics, TextStyle, Text} from 'pixi.js';

type BoxSize = number | 'auto';
type TextAlign = {horizontal?: 'left' | 'right' | 'center'; vertical?: 'top' | 'bottom' | 'center';};
type Padding = {left?: number; top?: number; right?: number; bottom?: number;};

export type TextBoxProperties = {
    text: string;
    textStyle?: Partial<TextStyle>;
    textAlign?: TextAlign;
    padding?: Padding;
    boxWidth: BoxSize;
    boxHeight: BoxSize;
    background: number;
    round: number;
};

export class TextBox extends Container {
    protected _text: Text;
    protected _boxWidth = 0;
    protected _boxHeight = 0;
    protected _background: Graphics = new Graphics();
    protected _properties: TextBoxProperties;

    constructor(props: TextBoxProperties) {
        super();
        this._properties = {...props};

        const {text, textStyle} = props;

        this._text = new Text(text, textStyle);

        this.addChild(this._background, this._text);

        this.updateLayout();

        this.name = 'TextBox';
    }

    protected updateLayout(): void {
        const {boxWidth, boxHeight, textAlign, padding} = this._properties;
        const layout = calculateLayout(this._text.width, this._text.height, boxWidth, boxHeight, padding, textAlign);

        this._boxWidth = layout.width;
        this._boxHeight = layout.height;

        this.drawBackground(layout.width, layout.height);

        this._text.x = layout.contentPosition.x;
        this._text.y = layout.contentPosition.y;
        this._text.style.align = textAlign?.horizontal;

        if (textAlign) {
            if (textAlign.horizontal) {
                if (textAlign.horizontal === 'right') {
                    this._text.anchor.x = 1;
                } else if (textAlign.horizontal === 'center') {
                    this._text.anchor.x = 0.5;
                }
            }

            if (textAlign.vertical) {
                if (textAlign.vertical === 'bottom') {
                    this._text.anchor.y = 1;
                } else if (textAlign.horizontal === 'center') {
                    this._text.anchor.y = 0.5;
                }
            }
        }
    }

    protected drawBackground(width: number, height: number): void {
        const {round = 0, background} = this._properties;

        this._background.clear();
        this._background.beginFill(background, 1);

        if (round > 0) {
            this._background.drawRoundedRect(0, 0, width, height, round);
        } else {
            this._background.drawRect(0, 0, width, height);
        }

        this._background.endFill();
    }

    protected updateBackground(): void {
        this.drawBackground(this._boxWidth, this._boxHeight);
    }

    public set background(value: number) {
        this._properties.background = value;
        this.updateBackground();
    }

    public get background(): number {
        return this._properties.background;
    }

    public set boxWidth(value: BoxSize) {
        this._properties.boxWidth = value;
        this.updateLayout();
    }

    public get boxWidth(): BoxSize {
        return this._properties.boxWidth;
    }

    public set boxHeight(value: BoxSize) {
        this._properties.boxHeight = value;
        this.updateLayout();
    }

    public get boxHeight(): BoxSize {
        return this._properties.boxHeight;
    }

    public set text(value: string) {
        this._text.text = value;
        this.updateLayout();
    }

    public get text(): string {
        return this._text.text;
    }

    public set textAlign(value: TextAlign) {
        this._properties.textAlign = value;
        this.updateLayout();
    }

    public get textAlign(): TextAlign {
        return this._properties.textAlign || {};
    }

    public set round(value: number) {
        this._properties.round = value;
        this.updateBackground();
    }

    public get round(): number {
        return this._properties.round;
    }

    public set padding(value: Padding) {
        this._properties.padding = value;
        this.updateLayout();
    }

    public get padding(): Padding {
        return this._properties.padding || {};
    }
}
